// Customer message templates, client twin of utils/messaging/templates.js (preview, placeholders
// and defaults in the messaging settings; the server renders what is sent). Keep everything
// below the header identical to the server file: test/notifications/templates.test.js compares
// both.

const MESSAGE_TYPES = [
  "appointment_confirmation",
  "appointment_rescheduled",
  "appointment_reminder",
  "on_the_way",
  "review_request"
];

// Placeholders per type, in the order WhatsApp template parameters {{1}}, {{2}}, … are sent.
const PLACEHOLDERS = {
  appointment_confirmation: [
    "kunde",
    "datum",
    "von",
    "bis",
    "adresse",
    "monteur",
    "firma",
    "telefon"
  ],
  appointment_rescheduled: [
    "kunde",
    "datum",
    "von",
    "bis",
    "adresse",
    "monteur",
    "firma",
    "telefon"
  ],
  appointment_reminder: ["kunde", "tag", "von", "bis", "adresse", "monteur", "firma", "telefon"],
  on_the_way: ["kunde", "monteur", "ankunft", "adresse", "firma", "telefon"],
  review_request: ["kunde", "link", "firma", "abmelden"]
};

const DEFAULT_TEMPLATES = {
  appointment_confirmation: {
    subject: "Terminbestätigung für den {{datum}}",
    text: "Guten Tag {{kunde}},\n\nhiermit bestätigen wir Ihren Termin am {{datum}} zwischen {{von}} und {{bis}} Uhr.\nAdresse: {{adresse}}\nIhr Monteur: {{monteur}}\n\nBei Fragen erreichen Sie uns unter {{telefon}}.\n\nMit freundlichen Grüßen\n{{firma}}"
  },
  appointment_rescheduled: {
    subject: "Ihr Termin wurde verschoben: neu am {{datum}}",
    text: "Guten Tag {{kunde}},\n\nIhr Termin wurde verschoben. Neuer Termin: {{datum}} zwischen {{von}} und {{bis}} Uhr.\nAdresse: {{adresse}}\nIhr Monteur: {{monteur}}\n\nBei Fragen erreichen Sie uns unter {{telefon}}.\n\nMit freundlichen Grüßen\n{{firma}}"
  },
  appointment_reminder: {
    subject: "Erinnerung: Wir kommen {{tag}} zwischen {{von}} und {{bis}} Uhr",
    text: "Guten Tag {{kunde}}, wir kommen {{tag}} zwischen {{von}} und {{bis}} Uhr zu Ihnen ({{adresse}}). Ihr Monteur: {{monteur}}. Fragen? {{telefon}} – {{firma}}"
  },
  on_the_way: {
    subject: "Ihr Monteur ist unterwegs",
    text: "Guten Tag {{kunde}}, Ihr Monteur {{monteur}} ist unterwegs, Ankunft ca. {{ankunft}} Uhr. {{firma}}, Tel. {{telefon}}"
  },
  review_request: {
    subject: "Wie zufrieden waren Sie mit {{firma}}?",
    text: "Guten Tag {{kunde}},\n\nvielen Dank für Ihren Auftrag! Wenn Sie zufrieden waren, freuen wir uns sehr über eine kurze Bewertung:\n{{link}}\n\nMit freundlichen Grüßen\n{{firma}}\n\nSie möchten keine Bewertungsanfragen mehr erhalten? {{abmelden}}"
  }
};

const MAX_SUBJECT = 200;
const MAX_TEXT = 2000;
const PLACEHOLDER = /\{\{\s*([a-zA-Z_]+)\s*\}\}/g;

/** Replaces {{name}} by vars.name; unknown or empty placeholders become "". */
const render = (template, vars = {}) =>
  String(template || "")
    .replace(PLACEHOLDER, (_, key) => {
      const value = vars[key];
      return value === undefined || value === null ? "" : String(value);
    })
    // A placeholder left empty must not leave "Ihr Monteur: ." or double spaces behind
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +([,.])/g, "$1")
    .trim();

/** Placeholders used in a template that the type does not offer (shown as a warning). */
const unknownPlaceholders = (type, template) => {
  const allowed = PLACEHOLDERS[type] || [];
  const found = [];
  String(template || "").replace(PLACEHOLDER, (_, key) => {
    if (!allowed.includes(key) && !found.includes(key)) found.push(key);
    return "";
  });
  return found;
};

/** The effective subject and text of a type: the company's override or the default. */
const templateFor = (type, overrides = {}) => {
  const base = DEFAULT_TEMPLATES[type];
  if (!base) throw new Error(`Unknown message type ${type}`);
  const own = (overrides && overrides[type]) || {};
  return {
    subject: String(own.subject || "").trim() || base.subject,
    text: String(own.text || "").trim() || base.text
  };
};

/** Cleans a template override from a settings form (length limits, known types only). */
const normalizeTemplates = input => {
  const out = {};
  for (const type of MESSAGE_TYPES) {
    const own = (input && input[type]) || {};
    out[type] = {
      subject: String(own.subject || "")
        .trim()
        .slice(0, MAX_SUBJECT),
      text: String(own.text || "")
        .trim()
        .slice(0, MAX_TEXT)
    };
  }
  return out;
};

// GSM 03.38 basic character set (+ the extension table, counted twice). Anything else makes the
// SMS UCS-2 with 70 characters per part.
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = "^{}\\[~]|€\f";

/** { encoding, characters, parts } of an SMS text, to show the cost of a template. */
const smsInfo = text => {
  const chars = Array.from(String(text || ""));
  const gsm = chars.every(c => GSM_BASIC.includes(c) || GSM_EXTENDED.includes(c));
  if (gsm) {
    const length = chars.reduce((n, c) => n + (GSM_EXTENDED.includes(c) ? 2 : 1), 0);
    return {
      encoding: "GSM-7",
      characters: length,
      parts: length <= 160 ? 1 : Math.ceil(length / 153)
    };
  }
  const length = chars.reduce((n, c) => n + (c.codePointAt(0) > 0xffff ? 2 : 1), 0);
  return {
    encoding: "UCS-2",
    characters: length,
    parts: length <= 70 ? 1 : Math.ceil(length / 67)
  };
};

/** Sample values for the template preview in the settings. */
const SAMPLE_VARS = {
  kunde: "Frau Schneider",
  datum: "Montag, 05.10.2026",
  tag: "morgen",
  von: "08:00",
  bis: "10:00",
  adresse: "Lindenstraße 12, 50674 Köln",
  monteur: "Jonas Becker",
  ankunft: "10:15",
  firma: "Muster Haustechnik GmbH",
  telefon: "0221 123456",
  link: "https://example.de/r/abc",
  abmelden: "https://example.de/r/abc/abmelden"
};

export {
  MESSAGE_TYPES,
  PLACEHOLDERS,
  DEFAULT_TEMPLATES,
  MAX_SUBJECT,
  MAX_TEXT,
  SAMPLE_VARS,
  render,
  unknownPlaceholders,
  templateFor,
  normalizeTemplates,
  smsInfo
};
